import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { useAuth } from '@/features/auth/store'
import { signUpShopper } from '@/features/marketplace/api'
import { storefrontAuthMessage } from '@/routes/storefront/authMessage'

/** The handful of dial codes the storefront actually ships to; see `_COUNTRY_CODES` on the API. */
const DIAL_CODES = [
  { code: '+91', label: '+91 (India)' },
  { code: '+1', label: '+1 (US/Canada)' },
  { code: '+44', label: '+44 (UK)' },
  { code: '+61', label: '+61 (Australia)' },
  { code: '+971', label: '+971 (UAE)' },
  { code: '+65', label: '+65 (Singapore)' },
] as const

/**
 * `min(8)` matches `SignupRequest.password` on the API. Deliberately the same number and
 * not a stricter one: a client rule the server does not share is a rule that rejects a
 * password the account would have accepted, with no way for the shopper to find out why.
 */
const schema = z.object({
  full_name: z.string().trim().min(1, 'Enter your name').max(255),
  email: z.string().min(1, 'Enter your email address').email('That is not a valid email address'),
  dial_code: z.string().trim().regex(/^\+[1-9]\d{0,3}$/, 'Pick a dial code'),
  // Digits only, no spaces or symbols; blank is allowed since the phone itself is optional.
  phone: z
    .string()
    .trim()
    .regex(/^$|^\d{6,14}$/, 'Enter digits only, 6-14 digits, no spaces or symbols')
    .optional(),
  password: z.string().min(8, 'Use at least 8 characters'),
})

type FormValues = z.infer<typeof schema>

/**
 * Registering as a shopper (M24-03).
 *
 * The account created here holds the `guest` role, which carries no permission on any
 * resource in the PRD section 9 matrix -- a shopper is an authenticated stranger, not a
 * member of staff with a small remit. The server pins that; this form cannot ask for a
 * role and does not have a field for one.
 *
 * `POST /marketplace/signup` returns a live session rather than a "now go and sign in"
 * message, because the shopper registering here is mid-basket. The token pair is handed
 * to `useAuth.adoptSession` so the rest of the app treats it as an ordinary session.
 */
export function StorefrontSignup() {
  const status = useAuth((s) => s.status)
  const adoptSession = useAuth((s) => s.adoptSession)
  const navigate = useNavigate()
  const location = useLocation()
  const [formError, setFormError] = useState<string | null>(null)

  const from = (location.state as { from?: string } | null)?.from ?? '/store'

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { dial_code: '+91' },
    // A garbage phone number should stop looking wrong the moment it is fixed, not just the
    // moment the form is submitted -- validating on submit alone leaves a shopper staring at
    // an error that only clears on the next click.
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  if (status === 'authenticated') return <Navigate to={from} replace />

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      const phone = values.phone?.trim() || null
      const tokens = await signUpShopper({
        email: values.email,
        full_name: values.full_name,
        password: values.password,
        // A dial code with no phone number is noise, not an account detail.
        dial_code: phone ? values.dial_code : undefined,
        phone,
      })
      await adoptSession(tokens)
      navigate(from, { replace: true })
    } catch (error) {
      setFormError(storefrontAuthMessage(error))
    }
  })

  return (
    <div className="mx-auto max-w-md space-y-4 py-6">
      <div className="text-center">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Create an account
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          So we know where to send your order.
        </p>
      </div>

      <Card>
        <CardBody>
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {formError && <Alert tone="error">{formError}</Alert>}

            <Field label="Full name" error={errors.full_name?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('full_name')}
                  autoComplete="name"
                  autoFocus
                  placeholder="Priya Nair"
                />
              )}
            </Field>

            <Field label="Email" error={errors.email?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('email')}
                  type="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                />
              )}
            </Field>

            <Field
              label="Phone"
              error={errors.phone?.message ?? errors.dial_code?.message}
              hint="Optional — used only for delivery."
            >
              {(props) => (
                <div className="flex gap-2">
                  <select
                    className="h-10 w-28 shrink-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    aria-label="Dial code"
                    {...register('dial_code')}
                  >
                    {DIAL_CODES.map((d) => (
                      <option key={d.code} value={d.code}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <Input
                    {...props}
                    {...register('phone', {
                      // Strip anything that isn't a digit as it's typed, rather than
                      // waiting for blur to say the letters someone just typed are invalid.
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/\D/g, '').slice(0, 14)
                      },
                    })}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="98765 43210"
                    className="flex-1"
                  />
                </div>
              )}
            </Field>

            <Field label="Password" error={errors.password?.message} hint="At least 8 characters.">
              {(props) => (
                <PasswordInput
                  {...props}
                  {...register('password')}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              )}
            </Field>

            <Button type="submit" className="w-full" loading={isSubmitting}>
              {isSubmitting ? 'Creating your account…' : 'Create account'}
            </Button>
          </form>
        </CardBody>
      </Card>

      <p className="text-center text-sm text-slate-500 dark:text-slate-400">
        Already have an account?{' '}
        <Link
          to="/store/login"
          state={location.state}
          className="font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Sign in
        </Link>
      </p>
    </div>
  )
}
